import {
  pgTable,
  text,
  timestamp,
  uuid,
  integer,
  boolean,
  jsonb,
  index,
  uniqueIndex,
} from "drizzle-orm/pg-core";
import { ENGAGEMENT_STATUSES } from "@ease/shared";
import type { EngagementStatus } from "@ease/shared";

// D07: FK constraints added. tenantId excluded (partition key in per-tenant DB).
import { people } from "./crm";

// Re-export engagement constants so existing consumers don't break
export { ENGAGEMENT_STATUSES, type EngagementStatus };

// ---------------------------------------------------------------------------
// Status enums
// ---------------------------------------------------------------------------

export const EMAIL_STATUSES = [
  "queued",
  "rendering",
  "sending",
  "sent",
  "delivered",
  "opened",
  "clicked",
  "bounced",
  "soft_bounced",
  "complained",
  "unsubscribed",
  "failed",
  "suppressed",
] as const;

export const LIST_SUBSCRIPTION_STATUSES = [
  "subscribed",
  "unsubscribed",
  "pending",
] as const;

export type EmailStatus = (typeof EMAIL_STATUSES)[number];

// ---------------------------------------------------------------------------
// Timestamp helper
// ---------------------------------------------------------------------------

const tz = { withTimezone: true } as const;

// ---------------------------------------------------------------------------
// Tables
// ---------------------------------------------------------------------------

export const emailTemplates = pgTable(
  "email_templates",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    name: text("name").notNull(),
    subject: text("subject").notNull(),
    htmlContent: text("html_content").notNull(),
    textContent: text("text_content"),
    liquidVariables: jsonb("liquid_variables").$type<string[]>().default([]),
    category: text("category").notNull().default("transactional"),
    createdBy: text("created_by"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_email_templates_tenant").on(table.tenantId),
    index("idx_email_templates_tenant_category").on(
      table.tenantId,
      table.category,
    ),
  ],
);

export interface DynamicContentSignalMatch {
  source: string;
  operator: "eq" | "gt" | "lt" | "gte" | "lte" | "contains" | "exists";
  value?: string;
}

export interface DynamicContentVariantRecord {
  id: string;
  label: string;
  subject?: string;
  body: string;
  priority: number;
  signalMatch: DynamicContentSignalMatch;
}

export const dynamicContentBlocks = pgTable(
  "dynamic_content_blocks",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    name: text("name").notNull(),
    description: text("description"),
    defaultVariantId: text("default_variant_id").notNull(),
    variants: jsonb("variants").$type<DynamicContentVariantRecord[]>().notNull().default([]),
    createdBy: text("created_by"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
    updatedAt: timestamp("updated_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_dynamic_content_blocks_tenant").on(table.tenantId),
    uniqueIndex("idx_dynamic_content_blocks_tenant_name").on(table.tenantId, table.name),
  ],
);

export const sentEmails = pgTable(
  "sent_emails",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    correlationId: text("correlation_id").notNull().unique(),
    personId: uuid("person_id")
      .references(() => people.id, { onDelete: "set null" }),
    templateId: uuid("template_id")
      .references(() => emailTemplates.id, { onDelete: "set null" }),
    templateSnapshot: jsonb("template_snapshot"),
    campaignId: uuid("campaign_id"),  // FK to campaigns deferred (cross-file, marketing.ts owns)
    workflowEnrollmentId: uuid("workflow_enrollment_id"),  // FK deferred (cross-file, marketing.ts owns)
    fromEmail: text("from_email").notNull(),
    fromName: text("from_name"),
    toEmail: text("to_email").notNull(),
    subject: text("subject").notNull(),
    status: text("status").notNull().default("queued"),
    backendType: text("backend_type").notNull().default("ses"),
    backendMessageId: text("backend_message_id"),
    sentAt: timestamp("sent_at", tz),
    deliveredAt: timestamp("delivered_at", tz),
    openedAt: timestamp("opened_at", tz),
    clickedAt: timestamp("clicked_at", tz),
    bouncedAt: timestamp("bounced_at", tz),
    complainedAt: timestamp("complained_at", tz),
    unsubscribedAt: timestamp("unsubscribed_at", tz),
    clickCount: integer("click_count").notNull().default(0),
    openCount: integer("open_count").notNull().default(0),
    error: text("error"),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    index("idx_sent_emails_tenant").on(table.tenantId),
    index("idx_sent_emails_correlation").on(table.correlationId),
    index("idx_sent_emails_tenant_status").on(table.tenantId, table.status),
    index("idx_sent_emails_tenant_sent_at").on(table.tenantId, table.sentAt),
    index("idx_sent_emails_person_id").on(table.personId),
  ],
);

export const lists = pgTable(
  "lists",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    tenantId: uuid("tenant_id").notNull(),
    name: text("name").notNull(),
    description: text("description"),
    doubleOptin: boolean("double_optin").notNull().default(false),
    welcomeEmailTemplateId: uuid("welcome_email_template_id")
      .references(() => emailTemplates.id, { onDelete: "set null" }),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [index("idx_lists_tenant").on(table.tenantId)],
);

// NOTE: emailDrafts / EMAIL_DRAFT_STATUSES / EmailDraftStatus live in the
// dedicated `./email-drafts.ts` module (AI-013). PR #1755 introduced a
// parallel definition here that collided with #1754's canonical file.
// The canonical schema is re-exported from `./index.ts` so `@ease/db/schema`
// continues to expose `emailDrafts` for consumers.

export const listSubscriptions = pgTable(
  "list_subscriptions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    listId: uuid("list_id")
      .notNull()
      .references(() => lists.id, { onDelete: "cascade" }),
    personId: uuid("person_id")
      .notNull()
      .references(() => people.id, { onDelete: "cascade" }),
    status: text("status").notNull().default("subscribed"),
    subscribedAt: timestamp("subscribed_at", tz).notNull().defaultNow(),
    unsubscribedAt: timestamp("unsubscribed_at", tz),
    source: text("source"),
  },
  (table) => [
    uniqueIndex("idx_list_subscriptions_list_person").on(
      table.listId,
      table.personId,
    ),
    index("idx_list_subscriptions_person").on(table.personId),
  ],
);

export const emailSuppressions = pgTable(
  "email_suppressions",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    emailHash: text("email_hash").notNull(),
    tenantId: uuid("tenant_id").notNull(),
    reason: text("reason").notNull(),
    suppressedAt: timestamp("suppressed_at", tz).notNull().defaultNow(),
    source: text("source"),
  },
  (table) => [
    uniqueIndex("idx_email_suppressions_tenant_hash").on(
      table.tenantId,
      table.emailHash,
    ),
  ],
);

export const globalSuppressions = pgTable("global_suppressions", {
  emailHash: text("email_hash").primaryKey(),
  reason: text("reason").notNull(),
  suppressedAt: timestamp("suppressed_at", tz).notNull().defaultNow(),
});
